import { supabase } from "../../../lib/supabase";

export interface Message {
  uuid: string;
  content: string;
  sender: "user" | "assistant";
  timestamp?: Date;
  resources?: any[];
}

interface ConversationState {
  messages: Message[];
  resources: any[];
}

const initialConversationState: ConversationState = {
  messages: [],
  resources: [],
};

export async function saveMessagesToSupabase(messages: Message[]) {
  const { data: user } = await supabase.auth.getUser();

  if (!user.user) {
    throw new Error("User not authenticated");
  }

  const { error } = await supabase.from("concierge_messages").upsert(
    {
      user_id: user.user.id,
      content: messages,
    },
    {
      onConflict: "user_id",
    }
  );

  if (error) {
    throw error;
  }
}

export async function updateConversationState(
  message: Message,
  conversationId: string
) {
  const { data: user } = await supabase.auth.getUser();

  if (!user.user) {
    throw new Error("User not authenticated");
  }

  // First, try to get existing conversation state
  const { data: existingRecord, error: fetchError } = await supabase
    .from("concierge_messages")
    .select("*")
    .eq("mentee_uuid", user.user.id)
    .eq("uuid", conversationId)
    .single();

  const existingState = existingRecord?.content;

  if (fetchError && fetchError.code !== "PGRST116") {
    throw fetchError;
  }

  let newState: ConversationState;

  if (!existingState) {
    newState = {
      ...initialConversationState,
      messages: [message],
    };
  } else {
    newState = {
      ...existingState,
      messages: [...existingState.messages, message],
    };
  }

  const contentArray = {
    messages: newState.messages,
    resources: newState.resources,
  };

  const payload = {
    uuid: conversationId,
    mentee_uuid: user.user.id,
    content: contentArray,
  };

  const { error: upsertError } = await supabase
    .from("concierge_messages")
    .upsert(payload, {
      onConflict: "uuid",
    });

  if (upsertError) {
    throw upsertError;
  }

  return newState;
}

export async function fetchLatestConversation() {
  const { data: user } = await supabase.auth.getUser();

  if (!user.user) {
    throw new Error("User not authenticated");
  }

  const { data, error } = await supabase
    .from("concierge_messages")
    .select("*")
    .eq("mentee_uuid", user.user.id)
    .order("created_at", { ascending: false })
    .limit(1)
    .single();

  if (error && error.code !== "PGRST116") {
    // PGRST116 is "no rows returned"
    throw error;
  }

  return data;
}
